/**
 * §7.4 作業設定。各頁籤依 `settings.*` 細分（系統設定已拆到 /admin/*）。
 *
 * 這一頁最重要的是量測點頁籤。§6.1：
 * 「**`settings.measurement.write` 是全系統風險最高的權限。**
 *  改動量測點的啟用狀態或粒度，會改變後續所有工單的資料品質，而且不影響已完成的工單。
 *  這個權限預設只給主管，且每次變更都：1. 跳確認窗說明後果
 *  2. 寫入 ConfigChanged 事件 3. 在稽核軌跡以警示色標示」
 *
 * §16.3 #2：**不要在正式站台「修好」兩個量測缺口。**
 * 那是設備團隊要自己決定的事，系統只負責讓後果看得見——所以停用的量測點
 * 會顯示「停用的後果是什麼」，而不是一個催你打開的紅點。
 */

import { useMemo, useState } from 'react'
import { ApiError, api } from '../api/client.js'
import { useQuery } from '../api/hooks.js'
import { useAuth } from '../auth/AuthContext.js'
import { GranularityBadge, WeightSourceBadge } from '../components/badges.js'
import { Alert, Button, Card, Empty, Field, Mono, Pill, Select, Table, Td, TextInput } from '../components/ui.js'
import { useConfig, useDict } from '../config/ConfigContext.js'
import type { Config, MeasurementPoint } from '../../../shared/types/config.js'
import { type FormMode, type ModelList, type ModelRow, ModelFormModal } from '../components/ModelForm.js'
import { count } from '../../../shared/lib/format.js'

type Tab = 'measurement' | 'stations' | 'containers' | 'scales' | 'materials' | 'models'

const TABS: { key: Tab; label: string; perm: string }[] = [
  { key: 'measurement', label: '量測點', perm: 'settings.measurement.write' },
  { key: 'stations', label: '站台與設備', perm: 'settings.station.write' },
  { key: 'containers', label: '容器與儲位', perm: 'settings.dictionary.write' },
  { key: 'scales', label: '秤具', perm: 'settings.dictionary.write' },
  { key: 'materials', label: '料號字典', perm: 'settings.dictionary.write' },
  { key: 'models', label: '型號基準', perm: 'settings.model.create' },
]

export function Settings() {
  const { can } = useAuth()
  // `perm` 原本宣告了卻沒用到，所有頁籤對每個進得來的人都顯示。
  // 現在只有 settings.model.create 的人（操作員、覆核者、檢視者）也進得來，
  // 就必須真的過濾——否則他們會看到一堆按不動的設定。
  const visibleTabs = TABS.filter((t) => can('settings.read') || can(t.perm))
  const [tab, setTab] = useState<Tab>(visibleTabs[0]?.key ?? 'models')
  const { config, reload } = useConfig()
  const cfgMeta = useQuery<{ scopes: { global: string[]; site: string[] } }>('/api/config')

  if (!config) return <p className="text-sm text-ink-500">載入設定…</p>

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`min-h-touch rounded-md border px-3 py-2 text-sm ${
              tab === t.key
                ? 'border-sky-700 bg-sky-700 text-white'
                : 'border-ink-300 bg-white text-ink-700 hover:bg-ink-50'}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'measurement' && <MeasurementTab config={config} onSaved={reload} />}
      {tab === 'stations' && <StationsTab config={config} />}
      {tab === 'containers' && <ContainersTab config={config} />}
      {tab === 'scales' && <ScalesTab config={config} />}
      {tab === 'materials' && <MaterialsTab config={config} globalKeys={cfgMeta.data?.scopes.global ?? []} />}
      {tab === 'models' && <ModelsTab config={config} globalKeys={cfgMeta.data?.scopes.global ?? []} />}
    </div>
  )
}

// ── 量測點（高風險）───────────────────────────────────────────────────────

function MeasurementTab({ config, onSaved }: { config: Config; onSaved: () => void }) {
  const { can } = useAuth()
  const dict = useDict()
  const editable = can('settings.measurement.write')
  const [pending, setPending] = useState<{ mp: MeasurementPoint; change: Partial<MeasurementPoint> } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const disabled = config.measurementPoints.filter((m) => !m.enabled)

  const apply = async () => {
    if (!pending) return
    setBusy(true)
    setError(null)
    try {
      const next = config.measurementPoints.map((m) =>
        m.measurementPointId === pending.mp.measurementPointId ? { ...m, ...pending.change } : m)
      await api.patch('/api/config/measurementPoints', {
        value: next,
        // §6.1 高風險變更要明確確認過
        acknowledged: true,
        note: describeChange(pending.mp, pending.change),
      })
      setSaved(`${pending.mp.measurementPointId}：${describeChange(pending.mp, pending.change)}`)
      setPending(null)
      onSaved()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <>
      {saved && <Alert tone="ok" title="已變更並寫入稽核軌跡">{saved}</Alert>}

      {/* §16.3 #2：不要「修好」缺口。這裡只讓後果看得見 */}
      {disabled.length > 0 && (
        <Alert tone="info" title={`目前有 ${disabled.length} 個量測點是停用狀態`}>
          <p>
            停用不是錯誤——它反映現場還沒有那台秤。系統不會替您決定要不要秤，
            但一定讓您看見後果：這些量測點對應的批不會有重量，站台結算會顯示
            「無法結算」並說明原因。
          </p>
          <ul className="mt-1 ml-4 list-disc">
            {disabled.map((m) => (
              <li key={m.measurementPointId}>
                <Mono>{m.measurementPointId}</Mono>
                {' '}{dict.station(m.stationId)} · {dict.material(m.materialCode)}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <Card
        title="量測點"
        subtitle={editable
          ? '改動會影響後續所有工單的資料品質，但不影響已完成的工單'
          : '唯讀——變更需要「量測點的啟用與粒度」權限（風險最高，預設只給主管）'}
      >
        <Table head={['代碼', '站台', '物料', '種類', '粒度', '重量來源', '小數位', '狀態', '']}>
          {config.measurementPoints.map((m) => (
            <tr key={m.measurementPointId} className={m.enabled ? '' : 'bg-ink-50'}>
              <Td><Mono>{m.measurementPointId}</Mono></Td>
              <Td className="text-xs">{dict.station(m.stationId)}</Td>
              <Td className="text-xs">{m.materialCode === 'ANY' ? '不限' : dict.material(m.materialCode)}</Td>
              <Td className="text-xs">
                {m.kind === 'receipt' ? '繳庫' : m.kind === 'issue' ? '領料' : '出廠'}
              </Td>
              <Td><GranularityBadge g={m.granularity} mpId={m.measurementPointId} /></Td>
              <Td><WeightSourceBadge src={m.weightSource} /></Td>
              <Td className="tabular-nums text-xs">{m.decimals}</Td>
              <Td>
                {m.enabled ? <Pill tone="emerald">啟用</Pill> : <Pill tone="ink">停用</Pill>}
                {m.requiresDualCheck && <Pill tone="violet" title="需雙人覆核">覆核</Pill>}
              </Td>
              <Td>
                {editable && (
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm"
                      onClick={() => setPending({ mp: m, change: { enabled: !m.enabled } })}>
                      {m.enabled ? '停用' : '啟用'}
                    </Button>
                    {m.materialCode !== 'ANY' && (
                      <Button size="sm"
                        onClick={() => setPending({
                          mp: m,
                          change: { granularity: m.granularity === 'panel' ? 'item' : 'panel' },
                        })}>
                        改{m.granularity === 'panel' ? '整批' : '逐片'}
                      </Button>
                    )}
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      {/* §6.1 第 1 點：跳確認窗說明後果 */}
      {pending && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-950/50 p-4">
          <div className="w-full max-w-lg rounded-lg border border-amber-300 bg-white p-5 shadow-xl">
            <h2 className="font-semibold text-ink-900">確認變更量測點</h2>
            <p className="mt-1 text-sm text-ink-700">
              <Mono>{pending.mp.measurementPointId}</Mono>
              （{dict.station(pending.mp.stationId)} · {dict.material(pending.mp.materialCode)}）
              　→　<strong>{describeChange(pending.mp, pending.change)}</strong>
            </p>

            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-semibold">這個變更的後果</p>
              <ul className="mt-1 ml-4 list-disc space-y-0.5">
                {consequences(pending.mp, pending.change).map((c, i) => <li key={i}>{c}</li>)}
                <li><strong>不影響已完成的工單</strong>——歷史資料保留當時的粒度</li>
                <li>會寫入稽核軌跡，並以警示色標示（誰改的、什麼時候改的查得到）</li>
              </ul>
            </div>

            {error && <div className="mt-3"><Alert tone="danger">{error}</Alert></div>}

            <div className="mt-4 flex gap-2">
              <Button variant="primary" disabled={busy} onClick={() => void apply()} className="flex-1">
                {busy ? '變更中…' : '我了解後果，確認變更'}
              </Button>
              <Button onClick={() => { setPending(null); setError(null) }} disabled={busy}>取消</Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function describeChange(mp: MeasurementPoint, c: Partial<MeasurementPoint>): string {
  if (c.enabled !== undefined) return c.enabled ? '啟用' : '停用'
  if (c.granularity !== undefined) return c.granularity === 'panel' ? '改為逐片秤' : '改為整批秤'
  return '變更'
}

function consequences(mp: MeasurementPoint, c: Partial<MeasurementPoint>): string[] {
  if (c.enabled === true) {
    return [
      '之後這個站的這種物料就要輸入重量，沒輸入不能過帳',
      '先前沒有重量的批可以用「補秤」補上，補完站台結算就會從「無法結算」變成正常',
    ]
  }
  if (c.enabled === false) {
    return [
      '之後這個站的這種物料**不會有重量**，站台結算會顯示「無法結算」',
      '全鏈平衡會少掉這一段，該段的損耗無法查核',
    ]
  }
  if (c.granularity === 'panel') {
    return [
      `之後這個量測點的畫面**自動變成逐片輸入**——${mp.stationId ?? ''} 的操作員要一片一片秤`,
      '逐片模式下事件數必須等於片數，少一片就不能過帳',
      '好處是解析度升為 R1，物料帳可以直接對應到單片',
    ]
  }
  return [
    '之後這個量測點改為整批秤——一車一個數字',
    '解析度會降為 R2（集合分攤），單片的數字變成按理論基準推算的',
  ]
}

// ── 其餘頁籤（唯讀檢視，§7.4「拆出系統設定；各頁籤依 settings.* 細分」）──

function StationsTab({ config }: { config: Config }) {
  const dict = useDict()
  return (
    <>
      <Card title="站台">
        <Table head={['代碼', '名稱', '狀態', '備註']}>
          {config.stations.map((s) => (
            <tr key={s.stationId} className={s.enabled ? '' : 'bg-ink-50 text-ink-400'}>
              <Td><Mono>{s.stationId}</Mono></Td>
              <Td>{s.label}</Td>
              <Td>{s.enabled ? <Pill tone="emerald">啟用</Pill> : <Pill tone="ink">停用</Pill>}</Td>
              <Td className="text-xs text-ink-500">{s.note ?? ''}</Td>
            </tr>
          ))}
        </Table>
      </Card>
      <Card title="設備實例" subtitle="§10.1 並行互斥以設備為單位，不是站台">
        <Table head={['代碼', '名稱', '站台', '狀態']}>
          {config.resources.map((r) => (
            <tr key={r.resourceId} className={r.enabled ? '' : 'bg-ink-50 text-ink-400'}>
              <Td><Mono>{r.resourceId}</Mono></Td>
              <Td>{r.label}</Td>
              <Td className="text-xs">{dict.station(r.stationId)}</Td>
              <Td>{r.enabled ? <Pill tone="emerald">啟用</Pill> : <Pill tone="ink">停用</Pill>}</Td>
            </tr>
          ))}
        </Table>
      </Card>
      <Card title="批量規則">
        {config.batchRules.length === 0 ? <Empty>—</Empty> : (
          <Table head={['站台', '下限', '上限', '依據']}>
            {config.batchRules.map((b) => (
              <tr key={b.stationId}>
                <Td>{dict.station(b.stationId)}</Td>
                <Td className="tabular-nums">{b.minCount ?? '—'}</Td>
                <Td className="tabular-nums">{b.maxCount ?? '—'}</Td>
                <Td className="text-xs text-ink-500">{b.source}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  )
}

function ContainersTab({ config }: { config: Config }) {
  const dict = useDict()
  return (
    <>
      <Card title="容器">
        <Table head={['代碼', '名稱', '皮重', '限用物料']}>
          {config.containers.map((c) => (
            <tr key={c.containerId}>
              <Td><Mono>{c.containerId}</Mono></Td>
              <Td>{c.label}</Td>
              <Td className="tabular-nums text-xs">
                {c.tareVaries
                  ? <Pill tone="amber" title="皮重不一致，需逐次輸入">逐次輸入</Pill>
                  : c.tareKg === null ? '—' : `${c.tareKg} kg`}
              </Td>
              <Td className="text-xs">
                {c.forMaterials ? c.forMaterials.map((m) => dict.materialShort(m)).join('、') : '不限'}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
      <Card title="儲位">
        <Table head={['代碼', '名稱']}>
          {config.locations.map((l) => (
            <tr key={l.locationId}>
              <Td><Mono>{l.locationId}</Mono></Td>
              <Td>{l.label}</Td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  )
}

function ScalesTab({ config }: { config: Config }) {
  return (
    <Card title="秤具">
      <Table head={['代碼', '名稱', '小數位', '狀態']}>
        {config.scales.map((s) => (
          <tr key={s.scaleId} className={s.enabled ? '' : 'bg-ink-50 text-ink-400'}>
            <Td><Mono>{s.scaleId}</Mono></Td>
            <Td>{s.label}</Td>
            <Td className="tabular-nums text-xs">{s.decimals}</Td>
            <Td>{s.enabled ? <Pill tone="emerald">啟用</Pill> : <Pill tone="ink">停用</Pill>}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  )
}

function MaterialsTab({ config, globalKeys }: { config: Config; globalKeys: string[] }) {
  return (
    <Card
      title="料號字典"
      subtitle="§9 規則 7：畫面上的顯示名稱一律讀這裡，不在元件裡寫死中文"
    >
      {globalKeys.includes('materialDict') && (
        <div className="mb-3">
          <Alert tone="info" title="這是全公司層級的設定">
            改動會影響所有廠區的顯示名稱。變更會記在稽核軌跡，並在每個廠區都看得到（標示「全公司」）。
          </Alert>
        </div>
      )}
      <Table head={['代碼', '顯示名稱', '簡稱', '階段', '本階段使用']}>
        {config.materialDict.map((m) => (
          <tr key={m.materialCode} className={m.activeInMockup ? '' : 'text-ink-400'}>
            <Td><Mono>{m.materialCode}</Mono></Td>
            <Td>{m.label}</Td>
            <Td className="text-xs">{m.shortLabel}</Td>
            <Td className="text-xs uppercase">{m.stage}</Td>
            <Td>{m.activeInMockup ? '✓' : '—'}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  )
}

// ── 型號基準 ────────────────────────────────────────────────────────────────
//
// 這個頁籤跟其他唯讀頁籤不一樣，它可以編輯，而且權限分三層（見 api/src/sites/modelSpecs.ts）：
//   新增暫定型號 / 編輯沒被用過的  settings.model.create      所有角色
//   編輯被用過的 / 覆核            settings.dictionary.write  主管
//
// 設計上最重要的一件事：**暫定型號不參與數字計算**。現場先建起來只是為了保住
// 「這批是哪個型號」這個資訊，分攤數字跟「未辨識」完全一樣。所以現場的人先建
// 一個型號不會弄髒任何帳——這一點畫面上要講清楚，不然沒有人敢按。

function ModelsTab({ config, globalKeys }: { config: Config; globalKeys: string[] }) {
  const dict = useDict()
  const { reload: reloadConfig } = useConfig()
  const list = useQuery<ModelList>('/api/config/model-specs')
  const [editing, setEditing] = useState<{ mode: FormMode; row?: ModelRow } | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const codes = useMemo(() => {
    const s = new Set<string>()
    for (const m of config.modelSpecs) for (const k of Object.keys(m.composition ?? {})) s.add(k)
    return [...s]
  }, [config.modelSpecs])

  const rows = list.data?.modelSpecs ?? []
  const pending = rows.filter((r) => r.status === 'provisional')

  const afterChange = (msg: string) => {
    setNotice(msg)
    setEditing(null)
    list.reload()
    reloadConfig()   // 型號下拉與理論重都讀 ConfigContext，要一起更新
  }

  return (
    <>
      {notice && <Alert tone="ok" title="已寫入稽核軌跡">{notice}</Alert>}

      {/* 待覆核佇列。主管一進來就要看到「有人在等你」 */}
      {pending.length > 0 && (
        <Card
          title={`待覆核的暫定型號（${count(pending.length)}）`}
          tone="warn"
          subtitle="現場先建起來、基準還沒補。它們目前不參與任何數字計算"
        >
          <Alert tone="info" title="暫定型號現在是什麼狀態">
            <p>
              掛著暫定型號的板子，分攤數字<strong>跟「未辨識」完全一樣</strong>——
              系統不會拿不完整的基準去算任何東西。補齊組成比例並覆核之後，
              <strong>後續</strong>的分攤才改用它自己的基準；已經過帳的數字不受影響。
            </p>
          </Alert>
          <div className="mt-3">
            <Table head={['型號', '品牌', '名稱', '功率', '申請人', '申請說明', '已被使用', '']}>
              {pending.map((m) => (
                <tr key={m.modelCode} className="bg-amber-50">
                  <Td><Mono>{m.modelCode}</Mono></Td>
                  <Td className="text-xs">{m.manufacturer || <span className="text-ink-300">—</span>}</Td>
                  <Td className="text-xs">{m.label}</Td>
                  <Td className="tabular-nums text-xs">{m.ratedW ? `${m.ratedW} W` : '—'}</Td>
                  <Td className="text-xs">{m.createdBy ?? '—'}</Td>
                  <Td className="text-xs text-ink-500">{m.note || '—'}</Td>
                  <Td className="text-xs">{m.usageLabel}</Td>
                  <Td>
                    {list.data?.canConfirm ? (
                      <Button size="sm" variant="primary"
                        onClick={() => setEditing({ mode: 'confirm', row: m })}>補基準並覆核</Button>
                    ) : m.canEdit ? (
                      <Button size="sm" onClick={() => setEditing({ mode: 'edit', row: m })}>編輯</Button>
                    ) : (
                      <span className="text-xs text-ink-400">等主管覆核</span>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </div>
        </Card>
      )}

      <Card
        title="型號基準"
        subtitle="分攤基準的來源。改動會影響後續的分攤計算，歷史資料由事件流保留當時的值"
        right={list.data?.canCreate
          ? <Button size="sm" variant="primary" onClick={() => setEditing({ mode: 'create' })}>
              ＋ 新增型號
            </Button>
          : undefined}
      >
        {globalKeys.includes('modelSpecs') && (
          <div className="mb-3">
            <Alert tone="warn" title="這是全公司層級的設定，而且會影響數字">
              型號基準是單片分攤的基準。改動會影響後續所有的分攤計算。
              投影重建時會從事件流還原「當時的基準」，所以歷史數字不會被改動——
              但這件事只有在變更走 ConfigChanged 事件時才成立，所以請一律從這個畫面改。
            </Alert>
          </div>
        )}

        {list.error && <Alert tone="danger" title="讀不到型號">{list.error.message}</Alert>}

        <Table head={['型號', '品牌', '名稱', '功率', '狀態', '使用中', '片數', '總重',
          ...codes.map((c) => dict.materialShort(c)), '']}>
          {rows.map((m) => (
            <tr key={m.modelCode} className={m.status === 'provisional' ? 'bg-amber-50' : ''}>
              <Td><Mono>{m.modelCode}</Mono></Td>
              <Td className="text-xs">{m.manufacturer || <span className="text-ink-300">—</span>}</Td>
              <Td className="text-xs">{m.label}</Td>
              <Td className="tabular-nums text-xs">{m.ratedW ? `${m.ratedW} W` : '—'}</Td>
              <Td>
                {m.status === 'provisional'
                  ? <Pill tone="amber" title="基準還沒補齊，目前不參與計算">暫定</Pill>
                  : <Pill tone="emerald">已覆核</Pill>}
              </Td>
              <Td className="text-xs text-ink-500">
                {m.used ? m.usageLabel : m.locked ? <span className="text-ink-400">未使用（已覆核）</span> : '—'}
              </Td>
              <Td className="tabular-nums text-xs">{m.cellCount || '—'}</Td>
              <Td className="tabular-nums">{m.totalKg ? `${m.totalKg} kg` : '—'}</Td>
              {codes.map((c) => (
                <Td key={c} className="tabular-nums text-xs">
                  {m.composition?.[c as never] !== undefined
                    ? `${((m.composition[c as never] as number) * 100).toFixed(1)}%`
                    : '—'}
                </Td>
              ))}
              <Td>
                {m.canEdit
                  ? <Button size="sm" onClick={() => setEditing({ mode: 'edit', row: m })}>編輯</Button>
                  : <span className="text-[11px] text-ink-400" title={m.lockedReason ?? undefined}>
                      🔒 需主管
                    </span>}
              </Td>
            </tr>
          ))}
        </Table>

        <p className="mt-3 text-xs text-ink-500">
          容差：站台損耗 ±{(config.tolerances.stationLossPct * 100).toFixed(1)}% ·
          拆出流佔比 ±{(config.tolerances.compositionPct * 100).toFixed(0)}% ·
          逐片離群 ±{(config.tolerances.outlierPct * 100).toFixed(0)}%
        </p>
      </Card>

      {editing && (
        <ModelFormModal
          mode={editing.mode}
          row={editing.row}
          existing={rows.map((r) => r.modelCode)}
          canConfirm={list.data?.canConfirm === true}
          onClose={() => setEditing(null)}
          onDone={afterChange}
        />
      )}
    </>
  )
}
